// SPDX-FileCopyrightText: 2026 Angels' Ware
// SPDX-License-Identifier: AGPL-3.0-or-later

import type { SVGProps } from "react";

// OllamaIcon renders Ollama's llama mark, traced from the logo Ollama serves at
// ollama.com/public/ollama.png. Shaped like a LucideIcon so it slots into the
// iconFor() registry, alongside ClaudeIcon and OpenAIIcon.
//
// Two things differ from its siblings, both on purpose:
//
//   - It defaults to currentColor rather than a brand hue. The mark is
//     monochrome and Ollama's own is black, which would disappear against the
//     dark theme.
//   - The path is straight-line segments, not curves: it came from a raster
//     source and was simplified until the shape stopped changing at icon sizes.
//     At 16-24px the facets are not resolvable, so do not "smooth" it by hand.
//
// If Ollama ever publishes a vector mark, replace the path wholesale.
type Props = SVGProps<SVGSVGElement> & {
  size?: number | string;
  color?: string;
  strokeWidth?: number;
};

export function OllamaIcon({ size = 16, color, ...rest }: Props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 181 256"
      fill={color ?? "currentColor"}
      fillRule="evenodd"
      role="img"
      aria-label="Ollama"
      {...rest}
    >
      <path d="M41 18 51 21 56 26 63 42 65 55 75 50 84 48 102 49 116 55 118 42 122 34 122 31 130 21 140 18 148 21 153 26 158 36 161 50 160 81 173 94 180 114 179 136 174 148 172 149 172 152 177 162 180 181 178 198 173 210 171 211 171 215 176 225 178 240 177 252 175 256 162 256 164 236 161 223 159 222 156 216 156 209 163 199 166 187 165 170 162 161 156 151 157 146 161 143 166 132 167 119 164 107 161 101 153 92 142 86 125 86 122 83 122 80 120 79 118 74 114 70 112 70 106 65 97 62 83 62 71 67 61 76 57 85 43 85 32 89 21 99 17 106 14 116 14 129 16 136 19 142 24 146 25 151 19 161 16 170 15 187 18 199 25 209 25 216 22 222 20 223 17 236 19 256 5 255 3 242 4 230 7 219 10 215 10 211 8 210 3 198 1 181 4 162 9 150 6 147 1 134 1 111 8 94 21 81 20 50 23 36 28 26 33 21ZM41 32 37 38 34 49 34 74 39 72 50 72 52 69 49 43 44 33ZM138 32 133 40 131 47 129 69 131 72 134 71 147 74 148 57 146 44 143 36 140 32ZM83 118 98 118 108 121 115 125 123 133 127 142 127 155 123 163 114 171 103 175 78 175 64 169 56 160 54 155 54 142 58 133 69 123ZM41 120 49 120 54 126 53 133 45 139 38 137 35 131 38 122ZM132 120 140 120 143 122 146 129 143 137 136 139 128 133 127 126ZM85 127 72 132 65 139 63 144 63 153 65 157 70 162 80 166 101 166 111 162 118 153 118 144 116 139 109 132 103 129 96 127ZM85 139 89 141 93 139 98 140 98 146 94 148 94 156 92 157 88 157 86 155 87 148 85 148 82 145 82 141Z" />
    </svg>
  );
}
